import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AckResult,
  DraftCellsWire,
  SpectateOver,
  SpectateState,
  SpectateUpdate,
} from "@mk/shared";
import { getSocket, serverNow } from "../../lib/socket.ts";
import { errorMessage, errors } from "../../i18n/index.ts";

/**
 * Arkadaşının maçını izleme.
 *
 * Harf yetkisi oyuncu bazında. Arkadaşın olanın gönderilmiş tahminleri ve
 * canlı taslağı gelir; ötekinin yalnızca renk desenleri görünür.
 */

export type SpectateStatus = "loading" | "watching" | "over" | "error";

interface Result {
  status: SpectateStatus;
  error: string | null;
  state: SpectateState | null;
  result: SpectateOver | null;
  remainingMs: number;
  leave: () => void;
}

interface SpectateDraftPayload {
  matchId: string;
  userId: string;
  row: number;
  cells: DraftCellsWire;
}

function applyDraft(state: SpectateState, payload: SpectateDraftPayload): SpectateState {
  return {
    ...state,
    players: state.players.map((player) => {
      if (
        player.user.id !== payload.userId ||
        player.guesses === null ||
        payload.row !== player.attempts
      ) {
        return player;
      }
      return {
        ...player,
        draft: payload.cells.every((cell) => cell === null)
          ? null
          : { row: payload.row, cells: payload.cells },
      };
    }),
  };
}

export function useSpectate(matchId: string, onFollowMatch: (matchId: string) => void): Result {
  const [status, setStatus] = useState<SpectateStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<SpectateState | null>(null);
  const [result, setResult] = useState<SpectateOver | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Ayrılırken sunucuya haber verebilmek için; state'e bağlı kalmasın.
  const joined = useRef(false);
  // Maç bittikten sonra canlı üyelik kapanır ama rövanş takibi ekran açık
  // kaldığı sürece sürer. Unmount'ta bunu da spectate:leave ile kapatırız.
  const tracking = useRef(false);
  const onFollowMatchRef = useRef(onFollowMatch);

  useEffect(() => {
    onFollowMatchRef.current = onFollowMatch;
  }, [onFollowMatch]);

  const leave = useCallback(() => {
    if (!joined.current && !tracking.current) return;
    joined.current = false;
    tracking.current = false;
    getSocket().emit("spectate:leave", { matchId });
  }, [matchId]);

  useEffect(() => {
    const socket = getSocket();
    let disposed = false;
    let joinVersion = 0;
    let phase: "joining" | "watching" | "over" = "joining";
    let transitioning = false;
    const pendingDrafts = new Map<string, SpectateDraftPayload>();

    function transitionTo(nextMatchId: string) {
      if (disposed || transitioning || nextMatchId === matchId) return;
      transitioning = true;
      joined.current = false;
      tracking.current = false;
      pendingDrafts.clear();
      setState(null);
      setResult(null);
      setError(null);
      setStatus("loading");
      onFollowMatchRef.current(nextMatchId);
    }

    function followRematch() {
      const version = ++joinVersion;
      socket.emit(
        "spectate:follow-rematch",
        { matchId },
        (res: AckResult<{ nextMatchId: string | null }>) => {
          if (disposed || transitioning || version !== joinVersion) return;
          if (!res?.ok) {
            tracking.current = false;
            return;
          }
          tracking.current = res.data.nextMatchId === null;
          if (res.data.nextMatchId) transitionTo(res.data.nextMatchId);
        },
      );
    }

    function join() {
      // Yeniden bağlantıda eski üyelik sunucuda artık yoktur; ack gelene dek
      // gelen ilk taslaklar aşağıdaki tamponda tutulmalı.
      joined.current = false;
      tracking.current = false;
      phase = "joining";
      const version = ++joinVersion;
      socket.emit("spectate:join", { matchId }, (res: AckResult<SpectateState>) => {
        if (disposed) {
          if (res?.ok) socket.emit("spectate:leave", { matchId });
          return;
        }
        if (version !== joinVersion) return;
        if (!res?.ok) {
          joined.current = false;
          setState(null);
          setError(res ? errorMessage(res) : errors.spectate_start_failed());
          setStatus("error");
          return;
        }
        joined.current = true;
        tracking.current = true;
        phase = "watching";
        let nextState = res.data;
        for (const draft of pendingDrafts.values()) nextState = applyDraft(nextState, draft);
        pendingDrafts.clear();
        setError(null);
        setResult(null);
        setState(nextState);
        setStatus("watching");
      });
    }

    const onUpdate = (payload: SpectateUpdate) => {
      if (payload.matchId !== matchId) return;
      setState((current) => {
        if (!current) return current;
        return {
          ...current,
          players: current.players.map((player) => {
            const withLimit = {
              ...player,
              attemptLimit: payload.attemptLimits[player.user.id] ?? player.attemptLimit,
            };
            if (player.user.id !== payload.userId) return withLimit;
            const rows = [...player.rows];
            rows[payload.row] = payload.pattern;

            /*
             * Harfler yalnızca "letters" seviyesinde geliyor. `guesses` null
             * ise bu izleyicinin harf görme yetkisi yok; paket zaten harfsiz.
             */
            let guesses = player.guesses;
            if (guesses && payload.word) {
              guesses = [...guesses];
              guesses[payload.row] = { word: payload.word, pattern: payload.pattern };
            }

            return {
              ...withLimit,
              rows,
              guesses,
              draft: null,
              attempts: Math.max(player.attempts, payload.row + 1),
              solved: player.solved || payload.solved,
            };
          }),
        };
      });
    };

    const onDraft = (payload: SpectateDraftPayload) => {
      if (payload.matchId !== matchId) return;
      // İlk harf izleyicisinin oyuncuyu tetiklediği taslak, ayrı soketlerde
      // `join` ack'inden önce gelebilir. En son taslağı state kurulana dek tut.
      if (!joined.current) {
        pendingDrafts.set(payload.userId, payload);
        return;
      }
      setState((current) => (current ? applyDraft(current, payload) : current));
    };

    const onDeadline = (payload: { matchId: string; endsAt: number }) => {
      if (payload.matchId !== matchId) return;
      setState((current) => (current ? { ...current, endsAt: payload.endsAt } : current));
    };

    const onOver = (payload: SpectateOver) => {
      if (payload.matchId !== matchId) return;
      joined.current = false;
      tracking.current = true;
      phase = "over";
      pendingDrafts.clear();
      setResult(payload);
      setStatus("over");
      // Sunucu canlı üyeliği rövanş bekleme kaydına dönüştürür. Bu çağrı
      // aynı zamanda ağ yarışı/yeniden bağlantıda kaydı güvenle yeniden kurar.
      followRematch();
    };

    const onReauthorize = (payload: { matchId: string }) => {
      if (payload.matchId !== matchId) return;
      // Sunucu eski odaları çoktan söktü. Aynı join yolu arkadaşlığı yeniden
      // hesaplar; ayrı bir artımlı yetki algoritması tutmuyoruz.
      joined.current = false;
      tracking.current = false;
      phase = "joining";
      pendingDrafts.clear();
      setState(null);
      setError(null);
      setStatus("loading");
      join();
    };

    const onRematch = (payload: { previousMatchId: string; matchId: string }) => {
      if (payload.previousMatchId !== matchId) return;
      transitionTo(payload.matchId);
    };

    const onConnect = () => {
      if (phase === "over") followRematch();
      else join();
    };

    socket.on("spectate:update", onUpdate);
    socket.on("spectate:draft", onDraft);
    socket.on("spectate:deadline", onDeadline);
    socket.on("spectate:over", onOver);
    socket.on("spectate:rematch", onRematch);
    socket.on("spectate:reauthorize", onReauthorize);

    join();
    // Bağlantı koptuysa odaya yeniden katılmak gerekiyor.
    socket.on("connect", onConnect);

    return () => {
      disposed = true;
      joinVersion += 1;
      socket.off("spectate:update", onUpdate);
      socket.off("spectate:draft", onDraft);
      socket.off("spectate:deadline", onDeadline);
      socket.off("spectate:over", onOver);
      socket.off("spectate:rematch", onRematch);
      socket.off("spectate:reauthorize", onReauthorize);
      socket.off("connect", onConnect);
      if (joined.current || tracking.current) {
        joined.current = false;
        tracking.current = false;
        socket.emit("spectate:leave", { matchId });
      }
    };
  }, [matchId]);

  // Saat oyuncularınkiyle aynı kaynaktan: sunucunun `endsAt`i.
  useEffect(() => {
    if (!state || status !== "watching") return;
    const tick = () => setRemainingMs(Math.max(0, state.endsAt - serverNow()));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [state, status]);

  return { status, error, state, result, remainingMs, leave };
}
